// Signup-source attribution (PR3).
//
// The marketing and docs sites capture first-touch (utm_* + document.referrer)
// into their own localStorage and decorate outbound portal links with those
// values as query params (see the inline script in each site's <head>). The
// portal reads them here — from the query string first, falling back to any
// first-touch record stored on the portal's own origin — classifies a channel,
// and hands a flat object to signup so it lands in raw_user_meta_data and, via
// the handle_new_user() trigger, on public.profiles.

import referrers from '../config/attribution-referrers.json';

// Key the marketing/docs/portal first-touch script writes to (per origin).
export const FIRST_TOUCH_KEY = 'vennio_ft';
// Where the Google-OAuth path stashes attribution across the redirect, since
// Supabase OAuth can't carry user metadata before the account exists.
export const PENDING_ATTR_KEY = 'vennio_attr_pending';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

function hostOf(url) {
  if (!url) return '';
  try { return new URL(url).hostname.toLowerCase(); } catch { return ''; }
}

/**
 * Classify a signup into a fixed channel set:
 *   paid | ai-referral | organic-docs | organic-marketing | direct
 * Paid wins (utm_medium is the explicit signal); then AI referrer; then our own
 * docs/marketing hosts; else direct (no referrer/utm, or an unrecognised
 * external referrer we can't attribute).
 */
export function classifyChannel({ referrer = '', utm_source = '', utm_medium = '' } = {}) {
  const medium = (utm_medium || '').toLowerCase();
  if (medium && referrers.paidMediums.includes(medium)) return 'paid';

  const host = hostOf(referrer);
  if (host && referrers.ai.includes(host)) return 'ai-referral';
  if (host === 'docs.vennio.app') return 'organic-docs';
  if (host === 'vennio.app' || host === 'www.vennio.app') return 'organic-marketing';

  if (!host && !utm_source) return 'direct';
  return 'direct';
}

function readFirstTouch() {
  try {
    const raw = localStorage.getItem(FIRST_TOUCH_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

/**
 * Build the flat attribution object for signup. `searchParams` is the portal
 * /auth URL's params (carried from marketing/docs); missing values fall back to
 * the portal-origin first-touch record. Only non-empty keys are returned.
 *
 * @param {URLSearchParams} searchParams
 * @returns {Record<string,string>}
 */
export function buildAttribution(searchParams) {
  const ft = readFirstTouch();
  const get = (k) => (searchParams?.get(k) || ft[k] || '').trim();

  const utm = {};
  for (const k of UTM_KEYS) {
    const v = get(k);
    if (v) utm[k] = v;
  }

  // Referrer/landing arrive as ?ref= / ?lp= from the decorating script, else
  // fall back to the portal's own first-touch, else the live document.referrer.
  const referrer = (searchParams?.get('ref') || ft.referrer || (typeof document !== 'undefined' ? document.referrer : '') || '').trim();
  const landing = (searchParams?.get('lp') || ft.landing || '').trim();

  const channel = classifyChannel({ referrer, utm_source: utm.utm_source, utm_medium: utm.utm_medium });
  const source = utm.utm_source || hostOf(referrer) || 'direct';

  const attr = { ...utm, signup_channel: channel, signup_source: source };
  if (referrer) attr.signup_referrer = referrer;
  if (landing) attr.signup_landing = landing;
  return attr;
}

/** Analytics-friendly subset for the PostHog signup event / identify. */
export function attributionEventProps(attr = {}) {
  return {
    channel: attr.signup_channel || 'direct',
    source: attr.signup_source || 'direct',
    utm_source: attr.utm_source || null,
    utm_medium: attr.utm_medium || null,
    utm_campaign: attr.utm_campaign || null,
    referrer: attr.signup_referrer || null,
  };
}
